import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { cx } from '@/components/ui/primitives'
import { track } from '@/lib/analytics'
import type { UrlState } from '@/lib/urlState'
import { urlForRecord } from './records'
import { useSearchRecords } from './useSearchRecords'
import type { Destination } from './CommandPalette'
import type { SearchRecord } from './types'

// cmdk, Radix's dialog and Fuse are about 20 kB gzipped between them, and most sessions
// never open the palette. Loading the chunk on hover means the click still feels instant
// for anyone who does.
const load = () => import('./CommandPalette')
const CommandPalette = lazy(() => load().then((m) => ({ default: m.CommandPalette })))

/** ⌘ on a Mac, Ctrl everywhere else. Only ever used to label the key. */
const isMac = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)

/** A keystroke typed into a text box is text, not a shortcut. */
const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null
  if (!el || !el.tagName) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

export function SearchButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerEnter={() => void load()}
      onFocus={() => void load()}
      aria-label="Search the tracker"
      title={`Search everything (${isMac() ? '⌘' : 'Ctrl '}K)`}
      className={cx(
        'inline-flex min-h-[36px] items-center gap-2 rounded px-2 py-2 text-sm',
        'bg-mc-surface text-mc-muted ring-1 ring-inset ring-mc-line',
        'transition-colors hover:bg-mc-surface-2 hover:text-mc-primary sm:px-3',
        className,
      )}
    >
      <Search size={15} aria-hidden />
      <span className="hidden lg:inline">Search…</span>
      <kbd className="hidden rounded border border-mc-line bg-mc-surface-2 px-1 py-0.5 font-mono text-xs lg:inline">
        {isMac() ? '⌘' : 'Ctrl '}K
      </kbd>
    </button>
  )
}

/**
 * The search, and the shortcut that opens it.
 *
 * Mounted for the whole session so the shortcut works from every tab, but it holds no
 * subscriptions and pulls no chunk until somebody opens it for the first time.
 */
export function GlobalSearch({
  open, onOpenChange, destinations, onGoTo, onNavigate,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  destinations: Destination[]
  onGoTo: (tabId: string) => void
  onNavigate: (next: Partial<UrlState>) => void
}) {
  // Sticky: once the collections are loaded, closing the palette should not throw them
  // away and make the next search wait again.
  const [everOpened, setEverOpened] = useState(open)
  useEffect(() => { if (open) setEverOpened(true) }, [open])

  const { records, loading, commentsUnavailable } = useSearchRecords(everOpened, open)

  const openPalette = useCallback(() => {
    track('search_opened')
    onOpenChange(true)
  }, [onOpenChange])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (open) onOpenChange(false)
        else openPalette()
        return
      }
      // `/` is the other shortcut everyone tries, and it is free as long as it never
      // steals a slash from somebody writing a note.
      if (e.key === '/' && !open && !isTyping(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        openPalette()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onOpenChange, openPalette])

  const openRecord = (record: SearchRecord) => {
    // The kind, never the query: a search is somebody's own words and often somebody's
    // name, and analytics events carry no personal data.
    track('search_result_opened', { kind: record.kind })
    onNavigate(urlForRecord(record))
    onOpenChange(false)
  }

  const goTo = (tabId: string) => {
    track('search_tab_opened', { tab: tabId })
    onGoTo(tabId)
    onOpenChange(false)
  }

  if (!open) return null

  return (
    <Suspense fallback={null}>
      <CommandPalette
        open
        onClose={() => onOpenChange(false)}
        records={records}
        loading={loading}
        commentsUnavailable={commentsUnavailable}
        destinations={destinations}
        onOpenRecord={openRecord}
        onGoTo={goTo}
      />
    </Suspense>
  )
}
